import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useFormik } from "formik";
import moment from "moment";
import { toast } from "react-toastify";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    RupeeIcon,
} from "../Styles/InputStyles";
import { FaRegCalendar } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MdEdit } from "react-icons/md";
import SelectOptionModal from "../SelectOptionModal";
import Config from "../../Config";
import { useSelector } from "react-redux";

const DuesModal = ({ selectedOption, setSelectedOption, addDuesMutate, addDuesLoading, selectedData, landlordData }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const [dueDateOpen, setDueDateOpen] = useState(false);
    const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);

    const propertiesOption = selectedData?.map(property => ({
        label: property.propertyName,
        value: property.propId,
    })) || [];

    const hasSingleProperty = propertiesOption.length === 1;
    const singlePropertyValue = hasSingleProperty ? propertiesOption[0].value : null;

    const formik = useFormik({
        initialValues: {
            amount: "",
            dueDate: "",
            description: "",
            properties: hasSingleProperty ? singlePropertyValue : "",
        },

        onSubmit: (values) => {
            if (!values.amount) {
                toast.error("Please enter amount");
                return;
            }
            if (values.amount <= 0) {
                toast.error("Amount must be greater than 0");
                return;
            }

            if (values.amount > 1000000) {
                toast.error("Amount cannot be greater than 10,00,000");
                return;
            }
            if (!values.dueDate) {
                toast.error("Please select Due date");
                return;
            }
            if (!values.properties) {
                toast.error("Please select a property");
                return;
            }

            addDuesMutate({
                amount: Number(values.amount),
                dueDate: moment(values.dueDate).format("YYYY-MM-DD"),
                description: values.description,
                isPaid: 0,
                type: Number(selectedOption),
                repetitionType: 1,
                paidDate: moment(values.dueDate).format("YYYY-MM-DD"),
                paymentMode: 1,
                paidToUserType: Config.USER_TYPE.LANDLORD,
                paidTo: landlordData?.id,
                paidBy: user?.id,
                paidByUserType: user?.userType,
                propertyIds: values.properties.toString()
            })
        },
    });

    useEffect(() => {
        if (hasSingleProperty && !formik.values.properties) {
            formik.setFieldValue("properties", singlePropertyValue);
        }
    }, [formik, hasSingleProperty, singlePropertyValue, formik.values.properties]);

    const handlePropertySelect = (selectedProperty) => {
        formik.setFieldValue("properties", selectedProperty);
        setIsPropertiesModalOpen(false);
    };
    const getCurrentPropertyLabel = () => {
        const currentProperty = formik.values.properties;
        if (hasSingleProperty && currentProperty === singlePropertyValue) {
            return propertiesOption[0].label;
        }
        if (!currentProperty) {
            return "Select property";
        }
        const selectedProperty = propertiesOption.find(prop => prop.value === currentProperty);
        return selectedProperty ? selectedProperty.label : "Select property";
    };

    return (
        <Container>
            <form className="px-1 flex flex-col gap-4" onSubmit={formik.handleSubmit}>
                <ScrollableContent className="!max-h-[calc(100vh-200px)]">
                    <Wrapper>
                        <InputGroup>
                            <Label htmlFor="amount">Amount *</Label>
                            <FieldWrappers>
                                <RupeeIcon />
                                <input
                                    type="text"
                                    id="amount"
                                    placeholder="Enter Amount"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    {...formik.getFieldProps("amount")}
                                    onKeyDown={(e) => {
                                        if (!/^\d$/.test(e.key) && e.key !== "Backspace") {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="pl-6"
                                />
                            </FieldWrappers>
                        </InputGroup>

                        <InputGroup>
                            <Label htmlFor="dueDate">Due Date *</Label>
                            <FieldWrappers>
                                <DatePicker
                                    selected={formik.values.dueDate ? new Date(formik.values.dueDate) : null}
                                    onChange={(date) => {
                                        formik.setFieldValue("dueDate", moment(date).format("YYYY-MM-DD"));
                                        setDueDateOpen(false);
                                    }}
                                    onClickOutside={() => setDueDateOpen(false)}
                                    onSelect={() => setDueDateOpen(false)}
                                    open={dueDateOpen}
                                    onInputClick={() => setDueDateOpen(true)}
                                    dateFormat="yyyy-MM-dd"
                                    className="items-center cursor-pointer"
                                    placeholderText="Select due date"
                                    showIcon
                                    icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                    showPopperArrow={false}
                                    wrapperClassName="w-full"
                                    name="dueDate"
                                    id="dueDate"
                                    autoComplete="off"
                                    onFocus={(e) => e.target.blur()}
                                    popperPlacement="bottom-start"
                                    portalId="root-portal"
                                    popperClassName="react-datepicker-popper"
                                />
                            </FieldWrappers>
                        </InputGroup>

                        <InputGroup>
                            <Label htmlFor="properties">
                                Property *

                            </Label>
                            <FieldWrappers
                                onClick={() => {
                                    if (!hasSingleProperty) {
                                        setIsPropertiesModalOpen(true);
                                    }
                                }}
                                className={`${hasSingleProperty ? 'bg-gray-50 cursor-default' : 'cursor-pointer hover:border-gray-400'} transition-colors`}
                            >
                                <input
                                    type="text"
                                    name="properties"
                                    id="properties"
                                    autoComplete="off"
                                    readOnly
                                    value={getCurrentPropertyLabel()}
                                    className={`w-full border-none outline-none ${hasSingleProperty ? 'bg-transparent cursor-default text-gray-700' : 'bg-transparent cursor-pointer placeholder-gray-400'}`}
                                    placeholder={hasSingleProperty ? "" : "Select a property"}
                                />
                                {!hasSingleProperty && (
                                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                )}
                            </FieldWrappers>
                        </InputGroup>

                        <InputGroup>
                            <Label htmlFor="description">Description</Label>
                            <FieldWrapper>
                                <Textarea
                                    id="description"
                                    placeholder="Enter Description"
                                    {...formik.getFieldProps("description")}
                                    rows={3}
                                />
                            </FieldWrapper>
                        </InputGroup>
                    </Wrapper>
                </ScrollableContent>

                <ActionSection>
                    <SideCancelBtn type="button" onClick={() => setSelectedOption(null)}>
                        Back
                    </SideCancelBtn>
                    <SubmitBtn type="submit" disabled={addDuesLoading}>
                        {addDuesLoading ? "Loading..." : "Add Dues"}
                    </SubmitBtn>
                </ActionSection>

                {!hasSingleProperty && (
                    <SelectOptionModal
                        isOpen={isPropertiesModalOpen}
                        onClose={() => setIsPropertiesModalOpen(false)}
                        title="Select Property"
                        options={propertiesOption}
                        selectedValue={formik.values.properties}
                        onSelect={handlePropertySelect}
                        searchPlaceholder="Search properties..."
                    />
                )}
            </form>
        </Container>
    );
};

const FieldWrapper = tw.div`w-full`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Textarea = tw.textarea`min-h-30 border rounded pl-2 py-2 w-full h-30 resize-y break-words overflow-hidden`;

export default DuesModal;